import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../services/api';
import Pagination from '../../components/common/Pagination';
import { useTranslation } from '../../context/LanguageContext';

function CompanyManagementPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // 통계용 (전체 데이터 기반)
    const [stats, setStats] = useState({
        totalCompanies: 0,
        activeCompanies: 0,
        totalUsers: 0,
        totalEquipments: 0
    });

    useEffect(() => {
        fetchCompanies();
    }, [page, size]);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/companies?page=${page}&size=${size}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success && result.data) {
                const data = result.data;
                setCompanies(data.content || []);
                setTotalElements(data.totalElements || 0);
                setTotalPages(data.totalPages || 0);

                const content = data.content || [];
                if (page === 0) {
                    setStats({
                        totalCompanies: data.totalElements || 0,
                        activeCompanies: content.filter(c => isCompanyActive(c)).length,
                        totalUsers: content.reduce((sum, c) => sum + (c.userCount || 0), 0),
                        totalEquipments: content.reduce((sum, c) => sum + (c.equipmentCount || 0), 0)
                    });
                }
            }
        } catch (error) {
            console.error('회사 목록 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('ko-KR');
    };

    const getCompanyStatus = (company) => {
        if (company.isActive === false) return 'INACTIVE';
        if (company.subscriptionStatus) return company.subscriptionStatus;
        return 'ACTIVE';
    };

    const isCompanyActive = (company) => {
        const status = getCompanyStatus(company);
        return status === 'ACTIVE' || status === 'TRIAL';
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    const handleDeleteCompany = async (company) => {
        const confirmMessage = t('pages.platform.companies.confirmDelete', { name: company.companyName });

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/companies/${company.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();

            if (result.success) {
                alert(t('pages.platform.companies.alertDeleted', { name: company.companyName }));
                fetchCompanies();
            } else {
                alert(t('pages.platform.companies.alertDeleteFail', { error: result.error || t('pages.platform.companies.alertUnknownError') }));
            }
        } catch (error) {
            console.error('회사 삭제 실패:', error);
            alert(t('pages.platform.companies.alertDeleteError'));
        }
    };

    return (
        <div className="platform-page">

            {/* 통계 */}
            <div className="platform-stats-grid">
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">business</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{stats.totalCompanies}</div>
                        <div className="platform-stat-label">{t('pages.platform.companies.stats.total')}</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">check_circle</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {companies.filter(c => isCompanyActive(c)).length}
                        </div>
                        <div className="platform-stat-label">{t('pages.platform.companies.stats.active')}</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">group</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {companies.reduce((sum, c) => sum + (c.userCount || 0), 0)}
                        </div>
                        <div className="platform-stat-label">{t('pages.platform.companies.stats.users')}</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">precision_manufacturing</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {companies.reduce((sum, c) => sum + (c.equipmentCount || 0), 0)}
                        </div>
                        <div className="platform-stat-label">{t('pages.platform.companies.stats.equipments')}</div>
                    </div>
                </div>
            </div>

            {/* 회사 목록 테이블 */}
            <div className="platform-table-container">
                <div className="platform-table-header">
                    <h3 className="platform-table-title">{t('pages.platform.companies.listTitle')}</h3>
                    <button className="platform-btn platform-btn-primary" onClick={() => navigate('/platform/companies/register')}>
                        <span className="material-symbols-outlined">add</span>
                        {t('pages.platform.companies.addBtn')}
                    </button>
                </div>
                {loading ? (
                    <div className="platform-empty">
                        <div className="platform-empty-text">{t('pages.platform.companies.loading')}</div>
                    </div>
                ) : companies.length === 0 ? (
                    <div className="platform-empty">
                        <span className="material-symbols-outlined platform-empty-icon">business</span>
                        <div className="platform-empty-text">{t('pages.platform.companies.empty')}</div>
                    </div>
                ) : (
                    <>
                        <div className="platform-table-scroll">
                        <table className="platform-table">
                            <thead>
                                <tr>
                                    <th>{t('pages.platform.companies.col.code')}</th>
                                    <th>{t('pages.platform.companies.col.name')}</th>
                                    <th>{t('pages.platform.companies.col.schema')}</th>
                                    <th>{t('pages.platform.companies.col.ceo')}</th>
                                    <th>{t('pages.platform.companies.col.users')}</th>
                                    <th>{t('pages.platform.companies.col.plan')}</th>
                                    <th>{t('pages.platform.companies.col.status')}</th>
                                    <th>{t('pages.platform.companies.col.createdAt')}</th>
                                    <th>{t('pages.platform.companies.col.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map((company) => (
                                    <tr key={company.id}>
                                        <td><strong>{company.companyCode}</strong></td>
                                        <td>{company.companyName}</td>
                                        <td><span className="platform-code">{company.schemaName}</span></td>
                                        <td className={company.representativeName ? '' : 'muted'}>{company.representativeName || '—'}</td>
                                        <td>{t('pages.platform.companies.usersSuffix', { n: company.userCount || 0 })}</td>
                                        <td className={company.subscriptionPlan ? '' : 'muted'}>{company.subscriptionPlan || '—'}</td>
                                        <td>
                                            <span className={`platform-badge ${isCompanyActive(company) ? 'active' : 'inactive'}`}>
                                                {getCompanyStatus(company)}
                                            </span>
                                        </td>
                                        <td>{formatDate(company.createdAt)}</td>
                                        <td>
                                            <div className="platform-actions">
                                                <button
                                                    className="platform-action-btn delete"
                                                    title={t('pages.platform.companies.actions.delete')}
                                                    onClick={() => handleDeleteCompany(company)}
                                                >
                                                    <span className="material-symbols-outlined">delete</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            size={size}
                            onPageChange={handlePageChange}
                            onSizeChange={handleSizeChange}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default CompanyManagementPage;
